import { decodeEventLog, decodeFunctionResult, encodeFunctionData, encodeFunctionResult, keccak256, type Address, type Hex } from "viem";
import { botnetRegistryV2Abi } from "../../../shared/src/abi-v2";
import type { DeploymentReference } from "./public-v2";
import type { V2AdmissionVoucher } from "./wallet-auth";

export type RecoveryRpc = {
  request(args: { method: string; params?: readonly unknown[] }, signal: AbortSignal): Promise<unknown>;
};
export type RecoveryRecord = {
  attemptId: string;
  deployment: DeploymentReference;
  payer: Address;
  name: string;
  voucher: V2AdmissionVoucher;
  activeAction?: Record<string, unknown> | null;
};

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function hex32(value: unknown): value is Hex {
  return typeof value === "string" && /^0x[0-9a-f]{64}$/i.test(value);
}
function quantity(value: unknown): bigint | null {
  if (typeof value === "bigint" && value >= 0n) return value;
  if (typeof value === "number" && Number.isSafeInteger(value) && value >= 0) return BigInt(value);
  if (typeof value === "string" && /^(?:0x(?:0|[1-9a-f][0-9a-f]*)|0|[1-9][0-9]*)$/i.test(value)) {
    try { return BigInt(value); } catch { return null; }
  }
  return null;
}
function equalAddress(a: unknown, b: unknown): boolean {
  return typeof a === "string" && typeof b === "string" && a.toLowerCase() === b.toLowerCase();
}
function equalHex(a: unknown, b: unknown): boolean {
  return typeof a === "string" && typeof b === "string" && a.toLowerCase() === b.toLowerCase();
}

type RefundedPurchaseIdentity = {
  deployment: DeploymentReference;
  payer: Address;
  reservationId: Hex;
  agentId: bigint;
};

/** Confirms a server-only paid row is terminal by reading the pinned registry at two canonical EIP-1898 anchors. */
export async function verifyRefundedTerminalPurchase(
  identity: RefundedPurchaseIdentity,
  rpc: RecoveryRpc,
  signal: AbortSignal,
): Promise<boolean> {
  const { deployment, payer, reservationId, agentId } = identity;
  if (signal.aborted || deployment.chainId !== 8453 || !hex32(reservationId) ||
      !/^0x[0-9a-f]{40}$/i.test(payer) || agentId < 0n || !/^0x[0-9a-f]{40}$/i.test(deployment.registryAddress) ||
      !hex32(deployment.registryRuntimeCodeHash)) return false;
  try {
    const chainId = quantity(await rpc.request({ method: "eth_chainId" }, signal));
    if (chainId !== 8453n) return false;
    const [finalizedRaw, latestRaw] = await Promise.all([
      rpc.request({ method: "eth_getBlockByNumber", params: ["finalized", false] }, signal),
      rpc.request({ method: "eth_getBlockByNumber", params: ["latest", false] }, signal),
    ]);
    const parseHead = (value: unknown) => record(value) ? { number: quantity(value.number), hash: value.hash } : null;
    const finalized = parseHead(finalizedRaw);
    const latest = parseHead(latestRaw);
    if (!finalized || !latest || finalized.number === null || latest.number === null ||
        !hex32(finalized.hash) || !hex32(latest.hash) || finalized.number > latest.number) return false;
    const anchors = [finalized, latest] as const;
    const codeResults = await Promise.all(anchors.map((head) => rpc.request({ method: "eth_getCode", params: [
      deployment.registryAddress, { blockHash: head.hash, requireCanonical: true },
    ] }, signal)));
    if (codeResults.some((code) => typeof code !== "string" || code.length > 49_154 || !/^0x(?:[0-9a-f]{2})+$/i.test(code) ||
        !equalHex(keccak256(code as Hex), deployment.registryRuntimeCodeHash))) return false;
    const callData = encodeFunctionData({ abi: botnetRegistryV2Abi, functionName: "purchase", args: [agentId] });
    const purchaseFunction = botnetRegistryV2Abi.find((item) => item.type === "function" && item.name === "purchase");
    const purchaseComponents = purchaseFunction?.outputs?.[0];
    if (!purchaseFunction || !purchaseComponents || !("components" in purchaseComponents)) return false;
    const purchases = await Promise.all(anchors.map(async (head) => {
      const raw = await rpc.request({ method: "eth_call", params: [
        { to: deployment.registryAddress, data: callData }, { blockHash: head.hash, requireCanonical: true },
      ] }, signal);
      if (typeof raw !== "string" || raw.length > 49_154 || !/^0x(?:[0-9a-f]{2})+$/i.test(raw)) return null;
      const decoded = decodeFunctionResult({ abi: botnetRegistryV2Abi, functionName: "purchase", data: raw as Hex });
      const canonical = encodeFunctionResult({ abi: botnetRegistryV2Abi, functionName: "purchase",
        result: purchaseComponents.components.map(({ name }) => decoded[name as keyof typeof decoded]) as never });
      return equalHex(canonical, raw) ? decoded : null;
    })) as unknown as Array<Record<string, unknown> | null>;
    const [finalizedPurchase, latestPurchase] = purchases;
    if (!finalizedPurchase || !latestPurchase) return false;
    const valid = (purchase: Record<string, unknown>) => purchase.exists === true && quantity(purchase.phase) === 5n &&
      equalAddress(purchase.payer, payer) && equalHex(purchase.reservationId, reservationId) &&
      typeof purchase.account === "string" && /^0x[0-9a-f]{40}$/i.test(purchase.account) &&
      !equalAddress(purchase.account, `0x${"0".repeat(40)}`) &&
      typeof purchase.capacitySlotId === "string" && hex32(purchase.capacitySlotId) &&
      quantity(purchase.slotEpoch) !== null && hex32(purchase.cleanupReceipt) &&
      !equalHex(purchase.cleanupReceipt, `0x${"0".repeat(64)}`) &&
      quantity(purchase.cleanupReceiptEpoch) === quantity(purchase.slotEpoch);
    if (!valid(finalizedPurchase) || !valid(latestPurchase)) return false;
    const stableAddresses = ["account", "payer"];
    const stableHashes = ["capacitySlotId", "cleanupReceipt", "reservationId"];
    const stableNumbers = ["slotEpoch", "cleanupReceiptEpoch", "phase"];
    if (stableAddresses.some((field) => !equalAddress(finalizedPurchase[field], latestPurchase[field])) ||
        stableHashes.some((field) => !equalHex(finalizedPurchase[field], latestPurchase[field])) ||
        stableNumbers.some((field) => quantity(finalizedPurchase[field]) !== quantity(latestPurchase[field]))) return false;
    const rechecked = await Promise.all(anchors.map((head) => rpc.request({ method: "eth_getBlockByNumber", params: [
      `0x${head.number!.toString(16)}`, false,
    ] }, signal)));
    return !signal.aborted && rechecked.every((raw, index) => {
      const head = parseHead(raw);
      const captured = anchors[index];
      return !!head && !!captured && head.number === captured.number && equalHex(head.hash, captured.hash);
    });
  } catch {
    return false;
  }
}

/** A public indexer row is only a candidate coordinate; chain evidence remains authoritative. */
export async function findPurchaseCandidate(
  lookup: { get(input: { reservationId: Hex; signal: AbortSignal }): Promise<unknown> },
  reservationId: Hex,
  signal: AbortSignal,
): Promise<{ transactionHash: Hex; agentId: bigint } | null> {
  const response = await lookup.get({ reservationId, signal });
  if (!record(response) || response.status !== 200 || !record(response.body) || response.body.schemaVersion !== 2 ||
      response.body.protocol !== "registry-v2" || !record(response.body.purchase)) return null;
  const purchase = response.body.purchase;
  const hash = purchase.paidTransactionHash;
  const id = quantity(purchase.agentId);
  if (!hex32(hash) || id === null || !equalHex(purchase.reservationId, reservationId)) return null;
  return { transactionHash: hash, agentId: id };
}

/** Verifies the exact submitted registration against its transaction, canonical receipt and V2 event. */
export async function verifyPurchaseReceipt(
  source: RecoveryRecord,
  action: Record<string, unknown>,
  transactionHash: Hex,
  rpc: RecoveryRpc,
  signal: AbortSignal,
): Promise<{ agentId: bigint; blockNumber: bigint; blockHash: Hex } | null> {
  const expected = action.transaction;
  if (!record(expected)) return null;
  const [transactionRaw, receiptRaw] = await Promise.all([
    rpc.request({ method: "eth_getTransactionByHash", params: [transactionHash] }, signal),
    rpc.request({ method: "eth_getTransactionReceipt", params: [transactionHash] }, signal),
  ]);
  if (!record(transactionRaw) || !record(receiptRaw)) return null;
  const tx = transactionRaw;
  const receipt = receiptRaw;
  if (!equalHex(tx.hash, transactionHash) || !equalAddress(tx.from, expected.from) || !equalAddress(tx.to, expected.to) ||
      !equalHex(tx.input ?? tx.data, expected.data) || !equalHex(tx.value, expected.value) ||
      (tx.chainId !== undefined && quantity(tx.chainId) !== BigInt(source.deployment.chainId)) ||
      !equalAddress(expected.to, source.deployment.registryAddress) || !equalAddress(expected.from, source.payer) ||
      !equalHex(receipt.transactionHash, transactionHash) || receipt.status !== "0x1") return null;
  const blockNumber = quantity(receipt.blockNumber);
  const blockHash = receipt.blockHash;
  if (blockNumber === null || !hex32(blockHash) || quantity(tx.blockNumber) !== blockNumber || !equalHex(tx.blockHash, blockHash)) return null;
  const canonical = await rpc.request({ method: "eth_getBlockByNumber", params: [`0x${blockNumber.toString(16)}`, false] }, signal);
  if (!record(canonical) || quantity(canonical.number) !== blockNumber || !equalHex(canonical.hash, blockHash)) return null;
  if (!Array.isArray(receipt.logs) || receipt.logs.length > 256) return null;
  const paid = receipt.logs.filter((item) => record(item) && equalAddress(item.address, source.deployment.registryAddress));
  let matched: { agentId: bigint } | null = null;
  for (const item of paid) {
    try {
      const decoded = decodeEventLog({ abi: botnetRegistryV2Abi, eventName: "PurchasePaid", data: item.data as Hex,
        topics: item.topics as [Hex, ...Hex[]], strict: true });
      const args = decoded.args as { agentId: bigint; creator: Address; payer: Address; principal: bigint; reservationId: Hex;
        capacitySlotId: Hex; slotEpoch: bigint };
      if (equalAddress(args.creator, source.voucher.creator) && equalAddress(args.payer, source.payer) &&
          args.principal === BigInt(source.voucher.price) && equalHex(args.reservationId, source.voucher.reservationId) &&
          equalHex(args.capacitySlotId, source.voucher.capacitySlotId) && args.slotEpoch === BigInt(source.voucher.slotEpoch)) {
        if (matched) return null;
        matched = { agentId: args.agentId };
      }
    } catch { /* unrelated registry log */ }
  }
  return matched ? { ...matched, blockNumber, blockHash } : null;
}

